import snakecaseKeys from "snakecase-keys";
import type { IDistributionRepository } from "~/interfaces/IDistributionRepository";
import { DistributionInfo, type DistributionType } from "~/entities/DistributionInfo";
import { RelatedContentsItem } from "~/entities/RelatedContentsItem";

interface PartInfo {
  partNumber: number;
  etag: string;
}

export class DistributionRepository implements IDistributionRepository {
  /**
   * 関連コンテンツ一覧取得
   */
  async getRelatedContents(contentsId: string): Promise<RelatedContentsItem[]> {
    const res = await GetRequest<{ statusCode: number; contents: any[] }>(
      `/contents_manage/${contentsId}/related_contents`,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to getRelatedContents");
    }
    return res.contents;
  }

  /**
   * 配信用のコンテンツファイルをアップロードする
   */
  async uploadContentFile(file: File, onProgress?: (now: number, max: number) => void): Promise<{ fileKey: string }> {
    // マルチパートアップロードに変更
    if (featureMultiPartUpload()) {
      return await this.uploadMultipartFile(file, onProgress);
    } else {
      return await this.uploadFile(`/contents_manage/contents/presigned_url`, file);
    }
  }

  /**
   * 配信用のサムネイルファイルをアップロードする
   */
  async uploadThumbnailFile(file: File): Promise<{ fileKey: string }> {
    return await this.uploadFile(`/contents_manage/thumbnail/presigned_url`, file);
  }

  /**
   * 配信コンテンツを投稿する
   */
  async postDistribution(
    distributionInfo: DistributionInfo,
  ): Promise<{ contentsId: string; contentsGroupKey: string }> {
    const contentType = this.getContentType(distributionInfo.type);

    const distributeAt =
      (distributionInfo.type === "live" || distributionInfo.type === "article") && !distributionInfo.distributeNow
        ? distributionInfo.distributeAt
        : null;

    // FIXME: 型エラーが出るのでanyにしている
    const chapters = distributionInfo.chapters ? snakecaseKeys(distributionInfo.chapters as any, { deep: true }) : [];

    const res = await PostRequest<{ statusCode: number; contentsId: string; contentsGroupKey: string }>(
      `/contents_manage`,
      JSON.stringify({
        contents_type: contentType,
        plan_id: distributionInfo.planId,
        is_free: distributionInfo.isFree,
        featured_flag: distributionInfo.isFeatured,
        title: distributionInfo.title,
        description: distributionInfo.detail,
        contents_key: distributionInfo.contentFile.fileKey,
        thumbnail_key: distributionInfo.thumbnailFile.fileKey,
        tags: distributionInfo.tags.map(tag => Number(tag.id)),
        library_ids: distributionInfo.libraries.map(library => library.libraryId),
        start_date: distributeAt ? this.convertDateString(distributeAt) : null, // 今すぐ配信の場合はnullを入れる
        movie_play_time_sec: distributionInfo.moviePlayTimeSec,
        publish_status: distributionInfo.publishStatus,
        post_user_id: distributionInfo.postUserId,
        article_body: distributionInfo.articleBody,
        article_file_keys: distributionInfo.articleFiles.map(file => file.fileKey),
        article_status: distributionInfo.articleStatus,
        contents_group_key: distributionInfo.contentsGroupKey,
        chapters: chapters,
        viewing_restriction_days: distributionInfo.viewingRestrictionDays,
        limited_plan_price_ids: distributionInfo.limitedPlanPriceIds,
      }),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to post distribution");
    }
    return { contentsId: res.contentsId, contentsGroupKey: res.contentsGroupKey };
  }

  /**
   * 配信コンテンツを変更する
   */
  async putDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const contentType = this.getContentType(distributionInfo.type);
    const thumbnailKey = distributionInfo.thumbnailFile.fileKey.includes("http")
      ? distributionInfo.thumbnailKey
      : distributionInfo.thumbnailFile.fileKey;
    const distributeAt =
      (distributionInfo.type === "live" || distributionInfo.type === "article") && !distributionInfo.distributeNow
        ? distributionInfo.distributeAt
        : null;
    // FIXME: 型エラーが出るのでanyにしている
    const chapters = distributionInfo.chapters ? snakecaseKeys(distributionInfo.chapters as any, { deep: true }) : [];
    const res = await PutRequest<{ statusCode: number }>(
      `/contents_manage/${distributionInfo.contentsId}`,
      JSON.stringify({
        contents_type: contentType,
        plan_id: distributionInfo.planId,
        is_free: distributionInfo.isFree,
        featured_flag: distributionInfo.isFeatured,
        title: distributionInfo.title,
        description: distributionInfo.detail,
        contents_key: distributionInfo.contentFile.fileKey,
        thumbnail_key: thumbnailKey,
        tags: distributionInfo.tags.map(tag => Number(tag.id)),
        library_ids: distributionInfo.libraries.map(library => library.libraryId),
        start_date: distributeAt ? this.convertDateString(distributeAt) : null,
        publish_status: distributionInfo.publishStatus,
        post_user_id: distributionInfo.postUserId,
        article_body: distributionInfo.articleBody,
        article_file_keys: distributionInfo.articleFiles.map(file => file.fileKey),
        article_status: distributionInfo.articleStatus,
        contents_group_key: distributionInfo.contentsGroupKey,
        chapters: chapters,
        viewing_restriction_days: distributionInfo.viewingRestrictionDays,
        limited_plan_price_ids: distributionInfo.limitedPlanPriceIds,
      }),
      true,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to put distribution");
    }
  }

  /**
   * 配信コンテンツを削除する
   */
  async deleteDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number; contentsId: string }>(
      `/contents_manage/${distributionInfo.contentsId}`,
      true,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to delete distribution");
    }
  }

  /**
   * 削除した配信コンテンツを復旧する
   */
  async recoverDistribution(distributionInfo: DistributionInfo): Promise<void> {
    const res = await PutRequest<{ statusCode: number; contentsId: string }>(
      `/contents_manage/${distributionInfo.contentsId}/cancel_delete`,
      "",
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to recoverDistribution");
    }
  }

  /*
   * コンテンツ取得用の署名付きURLを返却する
   */
  async getSignedUrl(fileKey: string): Promise<{ fileUrl: string; fileSubUrl: string }> {
    return await GetRequest<{ fileUrl: string; fileSubUrl: string }>(
      `/contents_manage/article_file?contents_key=${fileKey}`,
      false,
      true,
    );
  }

  /**
   * マルチパートアップロードを開始する
   */
  private async startMultipartUpload(mimeType: string): Promise<{ fileKey: string; uploadId: string }> {
    const res = await PostRequest<{ statusCode: number; fileKey: string; uploadId: string }>(
      `/contents_manage/contents/multipart_presigned_url/start`,
      JSON.stringify({ mime_type: mimeType }),
      true,
      false,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to start multipart upload");
    }
    return {
      fileKey: res.fileKey,
      uploadId: res.uploadId,
    };
  }

  /**
   * マルチパートアップロード用の署名付きURLを取得する
   */
  private async getMultipartPresignedUrl(
    uploadId: string,
    fileKey: string,
    partNumber: number,
  ): Promise<{ presignedUrl: string }> {
    const res = await GetRequest<{ statusCode: number; presignedUrl: string }>(
      `/contents_manage/contents/multipart_presigned_url/${uploadId}?file_key=${fileKey}&part_number=${partNumber}`,
      true,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to get multipart presigned URL");
    }
    return {
      presignedUrl: res.presignedUrl,
    };
  }

  /**
   * マルチパートアップロードを完了する
   */
  private async completeMultipartUpload(fileKey: string, uploadId: string, parts: PartInfo[]): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(
      `/contents_manage/contents/multipart_presigned_url/complete`,
      JSON.stringify({
        file_key: fileKey,
        upload_id: uploadId,
        etags: parts.map(part => ({
          part_number: part.partNumber,
          etag: part.etag,
        })),
      }),
      true,
      false,
      true,
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to complete multipart upload");
    }
  }

  /**
   * マルチパートアップロード用のファイルパートをアップロードする（リトライ付き）
   */
  private async uploadPart(presignedUrl: string, part: File): Promise<string> {
    const maxRetries = 3; // 最大3回リトライ
    const baseDelayMs = 1000; // リトライまでの間隔

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const res = await PutS3Request(presignedUrl, part);

        if (!res || res.statusCode !== 200) {
          throw new Error(`Upload failed with status ${res?.statusCode}`);
        }
        if (!res.etag) {
          throw new Error("Upload succeeded but etag is missing");
        }

        return res.etag;
      } catch (error) {
        if (attempt === maxRetries) {
          throw new Error(`Failed to upload part after ${maxRetries} attempts: ${(error as Error).message}`);
        }
        // Exponential backoff before retry
        const delay = baseDelayMs * 2 ** (attempt - 1);
        console.warn(`Retrying part upload (attempt ${attempt}) after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error("Unexpected error in uploadPart");
  }

  /**
   * マルチパートアップロードでファイルをアップロードする
   * @param file アップロードするファイル
   * @param onProgress 進捗コールバック (now, max)
   */
  async uploadMultipartFile(file: File, onProgress?: (now: number, max: number) => void): Promise<{ fileKey: string }> {
    // 1. マルチパートアップロードを開始
    const { fileKey, uploadId } = await this.startMultipartUpload(file.type);

    // 2. ファイルを分割
    const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    const parts: PartInfo[] = [];

    // 3. 最大5並列で各パートをアップロード
    let completed = 0;
    const concurrency = 5;
    let nextPart = 0;
    if (onProgress) onProgress(completed, totalParts);

    const uploadPartWorker = async () => {
      while (nextPart < totalParts) {
        /*
          JavaScriptのPromiseのthenはシングルスレッドのイベントループで逐次実行されるため、
          nextPart++とcompleted++の操作は競合状態(race condition)になりません。
        */
        const i = nextPart++;
        const partNumber = i + 1;
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        // 署名付きURLを取得
        const { presignedUrl } = await this.getMultipartPresignedUrl(uploadId, fileKey, partNumber);
        // パートをアップロード
        const etag = await this.uploadPart(presignedUrl, new File([chunk], file.name, { type: file.type }));
        parts[i] = { partNumber, etag };
        completed++;
        if (onProgress) onProgress(completed, totalParts);
      }
    };
    // 5並列でworkerを起動
    const workers = Array.from({ length: Math.min(concurrency, totalParts) }, () => uploadPartWorker());
    await Promise.all(workers);

    // 4. マルチパートアップロードを完了
    await this.completeMultipartUpload(fileKey, uploadId, parts);

    return { fileKey };
  }

  private async uploadFile(apiPath: string, file: File) {
    // 署名付きS3アップロードURLを取得する
    const mimeType = file.type;
    const presignedRes = await GetRequest<{
      statusCode: number;
      presignedUrl: string;
      fileKey: string;
    }>(`${apiPath}?mime_type=${mimeType}`);
    if (!presignedRes || presignedRes.statusCode !== 200) {
      throw new Error("Failed get presigned_url");
    }

    // S3 にアップロード
    const s3Res = await PutS3Request(presignedRes.presignedUrl, file);
    if (!s3Res || s3Res.statusCode !== 200) {
      throw new Error("Failed to put s3");
    }

    const fileKey = presignedRes.fileKey;
    return { fileKey };
  }

  private getContentType(distributionType: DistributionType) {
    switch (distributionType) {
      case "live":
        return "schedule";
      case "livearchive":
        return "live_movie";
      case "movie":
        return "movie";
      case "document":
        return "document";
      case "article":
        return "article";
    }
  }

  private convertDateString(dateString: string) {
    return new Date(dateString).toISOString();
  }
}
